<template>
  <v-container fluid pa-0>
    <v-row>
      <v-col
        cols="12"
        class="qr-scanner"
        :class="[{ 'scan-success': showSuccess }, { 'scan-error': showError }]"
      >
        <v-progress-circular v-if="loading" indeterminate />
        <template v-if="showOverlay">
          <div class="overlay"></div>
          <bm-icon
            :icon="showError ? 'Clear' : 'CheckCircleOutline'"
            color="white"
            class="state-icon"
          />
        </template>
        <html5-qr-stream @decoded="decoded($event)" />
        <slot
          name="error"
          :error-messages="internalErrorMessages"
          :current-scanned="currentScanned"
        >
          <barcode-scanner-error-dialog
            v-if="internalErrorMessages.length > 0"
            :error-messages="internalErrorMessages"
            @accept="internalErrorMessages = []"
            @cancel="internalErrorMessages = []"
          />
        </slot>
      </v-col>
    </v-row>
  </v-container>
</template>
<script setup lang="ts">
import Html5QrStream from "@/components/controls/device/barcodes/Html5QrStream.vue";
import BarcodeScannerErrorDialog from "@/components/controls/device/barcodes/BarcodeScannerErrorDialog.vue";
import appStore from "@/store/modules/appStore";
import { stripUrlPartsFromBarcode } from "@/components/controls/device/barcodeUtils";
import { barcodeSuccess, barcodeFailure } from "@/assets/sounds/barcodeSounds";
import { computed, ref, watch } from "vue";
import { useI18n } from "@/plugins/i18n";

interface BarcodeScanner2dProps {
  timeoutMs?: number;
  rules?: ((item: any) => boolean | string)[];
  playSounds?: boolean;
  snackbarTimeout?: number;
}

interface BarcodeScanner2dEvents {
  (eventName: "valid-scanned", value: string): void;
  (eventName: "loading", value: boolean): void;
}

const props = withDefaults(defineProps<BarcodeScanner2dProps>(), {
  timeoutMs: 1000,
  rules: () => [],
  playSounds: true,
  snackbarTimeout: 4000,
});
const emit = defineEmits<BarcodeScanner2dEvents>();
const { $m } = useI18n();

const loading = ref(false);
const showSuccess = ref(false);
const internalErrorMessages = ref<string[]>([]);
const currentScanned = ref("");

const showError = computed(() => internalErrorMessages.value.length > 0);
const showOverlay = computed(() => showError.value || showSuccess.value);

async function playScanSound(success: boolean) {
  const sound = success ? new Audio(barcodeSuccess) : new Audio(barcodeFailure);
  try {
    await sound.play();
  } catch (e) {
    // leave empty - no need to throw error/alert user
  }
}

async function decoded(decodedValue: string) {
  // Reject another event while we are still processing the last or we are showing errors
  if (showOverlay.value) {
    return;
  }

  // Strip the url parts out internally so we don't push errors
  decodedValue = stripUrlPartsFromBarcode(decodedValue);

  currentScanned.value = decodedValue;
  internalErrorMessages.value = [];

  props.rules.forEach((rule) => {
    const result = rule(currentScanned.value);
    if (typeof result === "string") {
      internalErrorMessages.value.push(result);
    } else if (!result) {
      internalErrorMessages.value.push($m.containers_unknown_error);
    }
  });

  if (props.playSounds) {
    await playScanSound(!showError.value);
  }

  if (!showError.value) {
    appStore.pushSnackbarConfig({
      text: $m.barcode_last_scanned + " " + currentScanned.value,
      timeout: props.snackbarTimeout,
    });

    showSuccess.value = true;
    setTimeout(() => {
      showSuccess.value = false;
      emit("valid-scanned", currentScanned.value);
    }, props.timeoutMs);
  }
}

watch(loading, (isLoading) => emit("loading", isLoading));
</script>

<style scoped lang="sass">
@import "src/sass/colours"

$successIconSize: 100px

.qr-scanner
  position: relative
  border: 5px solid transparent

.qr-scanner video
  max-width : 100%
  display : block

.qr-scanner.scan-success
  border: 5px solid $bm-color-success-medium

.qr-scanner.scan-error
  border: 5px solid $bm-color-error-medium

.qr-scanner .overlay
  height: 100%
  width: 100%
  position: absolute
  top: 0
  left: 0
  z-index: 2
  opacity: 0.50

.scan-success .overlay
  background: $bm-color-success-medium

.scan-error .overlay
  background: $bm-color-error-medium


// Center Alignment of absolute items are calculated from their top left corner
// Add Half of their size
.v-progress-circular, .v-icon.state-icon
  z-index: 3
  position: absolute
  left: 50%
  top: 50%
  vertical-align: middle

.v-icon.state-icon
  font-size: $successIconSize
  margin-top: calc(-#{$successIconSize} / 2)
  margin-left: calc(-#{$successIconSize} / 2)
</style>
